import { formatThaiShortDateTwoDigitYear } from 'common/utils/date'
import { useIsFishSpecie, useSpeciesName } from 'modules/pond/api/selector'
import { Topup } from 'modules/shrimpTopup/api/types'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { Box, Card, CardContent, Typography, Grid, Divider } from '@mui/material'

import ShrimpTopupViewDialog from '../ShrimpTopupViewDialog'

type Props = {
  topupsData: Topup[]
}

const ShrimpTopupRecordBodyTopup = ({ topupsData }: Props) => {
  const { t } = useTranslation('shrimpTopup')
  const species = useSpeciesName()
  const isFish = useIsFishSpecie()

  const [isDialogOpen, setIsDialogOpen] = React.useState(false)
  const [selectedTopupId, setSelectedTopupId] = React.useState<string | null>(null)

  const handleCardClick = (topupId: string) => {
    setSelectedTopupId(topupId)
    setIsDialogOpen(true)
  }

  const handleDialogClose = () => {
    setIsDialogOpen(false)
    setSelectedTopupId(null)
  }

  return (
    <>
      {topupsData.map((topup, index) => (
        <Grid item sm={12} md={6} xl={3} key={topup.id}>
          <Card
            key={topup.id}
            sx={{
              width: '100%',
              borderRadius: { xs: 1, sm: 2 },
              transition: 'transform 0.3s ease',
              cursor: 'pointer',
            }}
            onClick={() => handleCardClick(topup.id)}
          >
            <CardContent
              sx={{
                p: { xs: 1.5, sm: 2 },
              }}
            >
              <Box sx={{ mb: 2 }}>
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Typography variant="body2" component="div" fontWeight="bold">
                      {t('topup.additionalReleaseDate', { species })}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" align="right">
                      {formatThaiShortDateTwoDigitYear(topup?.topupDate ?? null, {
                        showTime: true,
                      })}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 0.5 }} />
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" component="div" fontWeight="bold">
                      {t('topup.salinity')}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" align="right">
                      {topup?.salinity} ppt
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 0.5 }} />
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" component="div" fontWeight="bold">
                      {t('topup.breedCondition', { species })}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" align="right">
                      {topup?.breedName}
                    </Typography>
                  </Grid>

                  <Grid item xs={6}>
                    <Typography variant="body2" component="div" fontWeight="bold">
                      {t('topup.brandName', { species })}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" align="right">
                      {topup?.brandName}
                    </Typography>
                  </Grid>

                  <Grid item xs={6}>
                    <Typography variant="body2" component="div" fontWeight="bold">
                      {t('topup.breedProductName', { species })}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" align="right">
                      {topup?.breedProductName}
                    </Typography>
                  </Grid>

                  <Grid item xs={6}>
                    <Typography variant="body2" component="div" fontWeight="bold">
                      {t('topup.initialShrimpCount', { species })}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" align="right">
                      {topup?.shrimpCount?.toLocaleString() ?? '0'} ตัว
                    </Typography>
                  </Grid>

                  {isFish && (
                    <>
                      <Grid item xs={6}>
                        <Typography variant="body2" component="div" fontWeight="bold">
                          {t('topup.averageSize')}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" align="right">
                          {topup?.averageSize != null
                            ? `${topup.averageSize.toLocaleString()} ${t('topup.gramPerOne')}`
                            : '-'}
                        </Typography>
                      </Grid>
                    </>
                  )}
                </Grid>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      ))}
      {isDialogOpen && selectedTopupId && (
        <ShrimpTopupViewDialog
          open={isDialogOpen}
          onClose={handleDialogClose}
          shrimpTopupId={selectedTopupId ?? ''}
        />
      )}
    </>
  )
}

export default ShrimpTopupRecordBodyTopup
